import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, KeyRound, ShieldAlert, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { loginWithPassword, loginWithPin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("password"); // "password" or "pin"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin");
    }
  }, [isAuthenticated, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await loginWithPassword(email, password);
    setLoading(false);
    if (res.success) {
      toast.success("Welcome back to Anwer Fancy Lace Admin Portal!");
      navigate("/admin");
    } else {
      setError(res.error || "Authentication failed");
      toast.error(res.error || "Authentication failed");
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await loginWithPin(pin);
    setLoading(false);
    if (res.success) {
      toast.success("Admin PIN verified. Access granted!");
      navigate("/admin");
    } else {
      setError(res.error || "Invalid PIN");
      toast.error(res.error || "Invalid PIN");
    }
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 py-12">
      <div 
        data-testid="admin-login-card"
        className="max-w-md w-full bg-white rounded-3xl border-2 border-[#D4AF37] p-8 shadow-2xl space-y-6"
      >
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-full bg-[#1C1917] text-[#D4AF37] flex items-center justify-center mx-auto border border-[#D4AF37]/50 shadow-md">
            <Lock className="w-6 h-6" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#996515] block">
            ANWER FANCY LACE
          </span>
          <h1 className="font-heading text-2xl font-bold text-stone-900">
            Admin Management Portal
          </h1>
          <p className="text-xs text-stone-500">
            Secure access for products, prices, stock, and customer orders.
          </p>
        </div>

        {/* Tab switch: Email/Password vs Quick PIN */}
        <div className="flex bg-[#FAF8F5] p-1 rounded-xl border border-stone-200 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setMode("password")}
            className={`flex-1 py-2 rounded-lg transition ${
              mode === "password" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:text-stone-900"
            }`}
          >
            Email & Password
          </button>
          <button
            type="button"
            onClick={() => setMode("pin")}
            className={`flex-1 py-2 rounded-lg transition ${
              mode === "pin" ? "bg-[#1C1917] text-white shadow-sm" : "text-stone-600 hover:text-stone-900"
            }`}
          >
            Quick 6-Digit PIN
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        {/* Form: Password Mode */}
        {mode === "password" ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 text-xs">
            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Admin Email</label>
              <input
                type="email"
                required
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@anwerlace.pk"
                className="w-full px-3.5 py-2.5 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-stone-800">Password</label>
              <input
                type="password"
                required
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full py-3 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Login to Admin Portal"}
            </button>
          </form>
        ) : (
          /* Form: PIN Mode */
          <form onSubmit={handlePinSubmit} className="space-y-4 text-xs">
            <div className="space-y-1">
              <label className="font-semibold text-stone-800">6-Digit Admin PIN</label>
              <input
                type="password"
                maxLength={6}
                required
                data-testid="admin-pin-input"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="123456"
                className="w-full text-center tracking-[0.5em] text-lg font-bold px-3.5 py-2.5 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-pin-login-btn"
              className="w-full py-3 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <KeyRound className="w-4 h-4" />
              <span>{loading ? "Checking PIN..." : "Unlock Dashboard with PIN"}</span>
            </button>
          </form>
        )}

        {/* Demo Credentials Notice — no values displayed in the UI to avoid public disclosure */}
        <div className="pt-4 border-t border-stone-200 text-center space-y-1">
          <span className="text-[11px] text-stone-400 block font-medium">
            Demo credentials available in the internal <code className="bg-stone-100 px-1 py-0.5 rounded">memory/test_credentials.md</code> for operators.
          </span>
          <span className="text-[10px] text-rose-700 font-semibold block">
            Rotate ADMIN_PASSWORD, ADMIN_PIN and JWT_SECRET before production.
          </span>
        </div>

      </div>
    </div>
  );
}
